// ═══════════════════════════════════════════════════════════════════════════════
// CLOSING STRATEGY ENGINE v1.0
// EnergiPro Solutions — Structured Deal Closing & Conversion System
// ═══════════════════════════════════════════════════════════════════════════════
// Triggers: Deal Health + Activity → Actions (Call, Escalate, Close, Reactivate)
// Timing: Hot 24h action | 3-5d escalate | 14d close/archive
// Statuses: Negotiation, Awaiting Confirmation, Won, Lost
// Emails: Execution Planning, Clarification, Reactivation
// ═══════════════════════════════════════════════════════════════════════════════

import { DealTemperature, DealIntelligenceRecord, TEMPERATURE_STYLES } from './dealIntelligenceEngine';

// ─── TYPES ───────────────────────────────────────────────────────────────────

export type ClosingStatus =
  | 'active'
  | 'negotiation'
  | 'awaiting_confirmation'
  | 'won'
  | 'lost'
  | 'archived';

export type TriggerAction =
  | 'call_now'
  | 'send_escalation'
  | 'continue_followup'
  | 'send_reactivation'
  | 'close_deal'
  | 'mark_negotiation'
  | 'recommend_close';

export type ClosingEmailType = 'execution_planning' | 'clarification' | 'reactivation';

export interface TriggerCondition {
  temperature: DealTemperature;
  hasResponse: boolean;
  daysWithoutResponse: number;
  action: TriggerAction;
  label: string;
  urgency: 'critical' | 'high' | 'medium' | 'low';
  description: string;
}

export interface TimingRule {
  id: string;
  condition: string;
  threshold: string;
  action: string;
  urgency: 'critical' | 'high' | 'medium' | 'low';
}

export interface ClosingEmail {
  type: ClosingEmailType;
  subject: string;
  body: string;
  label: string;
  description: string;
  triggerCondition: string;
}

export interface ClosingStageRecord {
  rfqId: string;
  referenceId: string;
  clientName: string;
  clientEmail: string;
  companyName: string;
  serviceName: string;
  dealHealthScore: number;
  temperature: DealTemperature;
  closingStatus: ClosingStatus;
  daysWithoutResponse: number;
  daysSinceProposal: number;
  lastActivityDate: string | null;
  triggeredAction: TriggerCondition;
  availableEmails: ClosingEmail[];
  interventionRequired: boolean;
  interventionReason?: string;
  timeline: ClosingTimelineEntry[];
  notes: string[];
  proposalValue?: string;
}

export interface ClosingTimelineEntry {
  id: string;
  timestamp: string;
  action: string;
  actor: string;
  details?: string;
}

// ─── CLOSING STATUS STYLES ───────────────────────────────────────────────────

export const CLOSING_STATUS_STYLES: Record<ClosingStatus, {
  bg: string; text: string; border: string; label: string; icon: string;
}> = {
  active: { bg: 'bg-green-500/10', text: 'text-green-300', border: 'border-green-500/30', label: 'Active', icon: '●' },
  negotiation: { bg: 'bg-purple-500/10', text: 'text-purple-300', border: 'border-purple-500/30', label: 'Negotiation', icon: '🤝' },
  awaiting_confirmation: { bg: 'bg-amber-500/10', text: 'text-amber-300', border: 'border-amber-500/30', label: 'Awaiting Confirmation', icon: '⏳' },
  won: { bg: 'bg-emerald-500/10', text: 'text-emerald-300', border: 'border-emerald-500/30', label: 'Won', icon: '✅' },
  lost: { bg: 'bg-red-500/10', text: 'text-red-300', border: 'border-red-500/30', label: 'Lost', icon: '✗' },
  archived: { bg: 'bg-slate-500/10', text: 'text-slate-400', border: 'border-slate-500/30', label: 'Archived', icon: '📁' },
};

// ─── TIMING RULES ────────────────────────────────────────────────────────────

export const TIMING_RULES: TimingRule[] = [
  { id: 'TR-01', condition: 'Hot deal detected', threshold: 'Within 24 hours', action: 'Call client immediately', urgency: 'critical' },
  { id: 'TR-02', condition: 'Hot deal + No response', threshold: '3 days', action: 'Send escalation email', urgency: 'high' },
  { id: 'TR-03', condition: 'Any deal + No response', threshold: '5 days', action: 'Escalate to management', urgency: 'high' },
  { id: 'TR-04', condition: 'Warm deal + Active', threshold: 'Standard schedule', action: 'Continue controlled follow-up', urgency: 'medium' },
  { id: 'TR-05', condition: 'Cold deal + No response', threshold: '14 days', action: 'Close or archive', urgency: 'low' },
  { id: 'TR-06', condition: 'Cold deal + Some activity', threshold: '7 days', action: 'Send reactivation email', urgency: 'medium' },
];

// ─── TRIGGER ENGINE ──────────────────────────────────────────────────────────

export function evaluateTrigger(temperature: DealTemperature, daysWithoutResponse: number, hasRecentActivity: boolean): TriggerCondition {
  // Hot + Active → Call Now
  if (temperature === 'hot' && hasRecentActivity) {
    return {
      temperature,
      hasResponse: true,
      daysWithoutResponse,
      action: 'call_now',
      label: 'Call Now — High Probability',
      urgency: 'critical',
      description: 'Client is actively engaged. Immediate personal contact will accelerate conversion.',
    };
  }

  // Hot + No Response → Escalation
  if (temperature === 'hot' && !hasRecentActivity && daysWithoutResponse >= 3) {
    return {
      temperature,
      hasResponse: false,
      daysWithoutResponse,
      action: 'send_escalation',
      label: 'Send Escalation Email',
      urgency: 'high',
      description: 'High-value deal with no recent response. Escalation email recommended to maintain momentum.',
    };
  }

  // Hot + Short silence → Mark Negotiation
  if (temperature === 'hot' && daysWithoutResponse < 3) {
    return {
      temperature,
      hasResponse: false,
      daysWithoutResponse,
      action: 'mark_negotiation',
      label: 'Move to Negotiation',
      urgency: 'high',
      description: 'Strong signals detected. Recommend marking as Negotiation stage.',
    };
  }

  // Warm → Continue
  if (temperature === 'warm' && daysWithoutResponse < 7) {
    return {
      temperature,
      hasResponse: hasRecentActivity,
      daysWithoutResponse,
      action: 'continue_followup',
      label: 'Continue Follow-Up',
      urgency: 'medium',
      description: 'Moderate engagement. Maintain controlled follow-up schedule.',
    };
  }

  // Warm + Extended silence
  if (temperature === 'warm' && daysWithoutResponse >= 7) {
    return {
      temperature,
      hasResponse: false,
      daysWithoutResponse,
      action: 'send_reactivation',
      label: 'Send Reactivation Email',
      urgency: 'medium',
      description: 'Engagement cooling. Reactivation email with value proposition recommended.',
    };
  }

  // Cold + 14+ days → Close
  if (temperature === 'cold' && daysWithoutResponse >= 14) {
    return {
      temperature,
      hasResponse: false,
      daysWithoutResponse,
      action: 'recommend_close',
      label: 'Recommend Close / Archive',
      urgency: 'low',
      description: 'No engagement for 14+ days. Consider closing or archiving this deal.',
    };
  }

  // Cold → Reactivation
  return {
    temperature,
    hasResponse: hasRecentActivity,
    daysWithoutResponse,
    action: 'send_reactivation',
    label: 'Trigger Reactivation',
    urgency: 'medium',
    description: 'Low engagement detected. Send reactivation sequence to re-engage client.',
  };
}

// ─── EMAIL TEMPLATES ─────────────────────────────────────────────────────────

export function generateClosingEmails(record: ClosingStageRecord): ClosingEmail[] {
  const { clientName, companyName, serviceName, referenceId } = record;
  const firstName = clientName.split(' ')[0];

  const emails: ClosingEmail[] = [
    {
      type: 'execution_planning',
      label: 'Execution Planning Email',
      description: 'Sent to hot deals — outlines next steps for project execution',
      triggerCondition: 'Hot deal with active engagement',
      subject: `${referenceId} — Execution Planning & Next Steps`,
      body: `Dear ${firstName},

Thank you for your continued engagement regarding ${serviceName} for ${companyName}.

Your request has been prioritized under our engineering workflow. Based on our technical discussions and your recent feedback, we are prepared to move forward with execution planning. Configuration selected based on field-proven performance under similar operating conditions.

━━━ EXECUTION READINESS ━━━
• Equipment Availability: Confirmed
• Engineering Review: Completed
• Mobilization Readiness: Immediate upon approval
━━━━━━━━━━━━━━━━━━━━━━━━━━

Below is our proposed next steps:

1. Technical Alignment Meeting — Schedule within 48 hours
2. Commercial Terms Confirmation — Pricing & payment structure
3. Mobilization Timeline — Equipment availability & logistics
4. HSE Documentation — Pre-mob safety package

Our engineering team is prepared to initiate mobilization planning upon your confirmation. Please let us know your preferred meeting slot this week.

You can track the status of your RFQ at any time through our Client Portal — providing real-time visibility on progress, documents, and next steps.

We are committed to delivering a seamless execution experience.

Best regards,
Business Development Team
EnergiPro Solutions
rfq@energiprogroup.com`,
    },
    {
      type: 'clarification',
      label: 'Clarification Email',
      description: 'Sent when client needs to clarify requirements or concerns',
      triggerCondition: 'Warm deal needing push or Hot deal with pending questions',
      subject: `${referenceId} — Quick Clarification Needed`,
      body: `Dear ${firstName},

I wanted to follow up on our proposal for ${serviceName} (Ref: ${referenceId}).

Your request has been prioritized under our engineering workflow. We noticed your team has been reviewing the documentation, and we want to ensure everything aligns with your requirements. If there are any:

• Technical specifications that need adjustment
• Commercial terms to discuss
• Timeline concerns to address
• Additional documentation needed

Please don't hesitate to reach out. Our engineering team is available for a 15-minute call to clarify any points.

━━━ EXECUTION READINESS ━━━
• Equipment Availability: Confirmed
• Engineering Review: Completed
• Mobilization Readiness: Immediate upon approval
━━━━━━━━━━━━━━━━━━━━━━━━━━

Configuration selected based on field-proven performance under similar operating conditions. We value this opportunity and are prepared to deliver exactly what ${companyName} needs.

Track your RFQ status anytime through our Client Portal for real-time visibility on progress and next steps.

Best regards,
Business Development Team
EnergiPro Solutions
rfq@energiprogroup.com`,
    },
    {
      type: 'reactivation',
      label: 'Reactivation Email',
      description: 'Sent to cold/stale deals to re-engage the client',
      triggerCondition: 'Cold deal or 7+ days without response',
      subject: `${referenceId} — Still Available for ${companyName}`,
      body: `Dear ${firstName},

I hope this message finds you well. I wanted to check in regarding our proposal for ${serviceName} (Ref: ${referenceId}).

We understand that priorities shift, and we want you to know:

✓ Our proposal remains valid and equipment is available
✓ We can accommodate updated timelines if your schedule has changed
✓ Any scope modifications can be addressed quickly

━━━ EXECUTION READINESS (CONFIRMED) ━━━
• Equipment Availability: Confirmed & Reserved
• Engineering Review: Completed
• Mobilization Readiness: Immediate upon approval
• Configuration: Selected based on field-proven performance under similar operating conditions
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

If this project is no longer proceeding, we understand — please let us know so we can update our records.

If you're still evaluating, we'd be happy to:
• Provide a revised proposal based on new requirements
• Arrange a technical discussion with our engineering team
• Share additional case studies relevant to your project

You can track the status of your RFQ and take actions directly through our Client Portal — available 24/7 for real-time updates.

Looking forward to hearing from you.

Best regards,
Business Development Team
EnergiPro Solutions
rfq@energiprogroup.com`,
    },
  ];

  return emails;
}

// ─── INTERVENTION REQUIREMENTS ───────────────────────────────────────────────

export function requiresHumanIntervention(status: ClosingStatus, temperature: DealTemperature): { required: boolean; reason?: string } {
  // Final negotiation always requires human
  if (status === 'negotiation') {
    return { required: true, reason: 'Final negotiation stage — requires manual commercial discussion' };
  }
  // Awaiting confirmation needs human follow-through
  if (status === 'awaiting_confirmation') {
    return { required: true, reason: 'Contract stage — requires manual confirmation and document preparation' };
  }
  // Hot deals in active stage need immediate human action
  if (temperature === 'hot' && status === 'active') {
    return { required: true, reason: 'High-probability deal — immediate personal contact required within 24h' };
  }
  return { required: false };
}

// ─── MOCK DATA GENERATOR ─────────────────────────────────────────────────────

function generateId(): string {
  return `CS-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
}

function daysAgo(d: number): string {
  const date = new Date();
  date.setDate(date.getDate() - d);
  return date.toISOString();
}

export function generateMockClosingData(): ClosingStageRecord[] {
  const records: ClosingStageRecord[] = [
    // ─── HOT DEALS (IMMEDIATE ACTION) ─────────────────────────────────────
    {
      rfqId: 'RFQ-001',
      referenceId: 'EPI-2026-0001',
      clientName: 'Ahmed Al-Rashid',
      clientEmail: 'a.rashid@aramco-ops.com',
      companyName: 'Saudi Aramco Operations',
      serviceName: 'Surface Well Testing',
      dealHealthScore: 92,
      temperature: 'hot',
      closingStatus: 'negotiation',
      daysWithoutResponse: 1,
      daysSinceProposal: 6,
      lastActivityDate: daysAgo(1),
      triggeredAction: evaluateTrigger('hot', 1, true),
      availableEmails: [],
      interventionRequired: true,
      interventionReason: 'Final negotiation stage — requires manual commercial discussion',
      timeline: [
        { id: generateId(), timestamp: daysAgo(6), action: 'Proposal Sent', actor: 'System' },
        { id: generateId(), timestamp: daysAgo(5), action: 'Email Opened', actor: 'Client' },
        { id: generateId(), timestamp: daysAgo(4), action: 'Portal Visit', actor: 'Client' },
        { id: generateId(), timestamp: daysAgo(3), action: 'Revision Requested', actor: 'Client', details: 'Updated pressure specs' },
        { id: generateId(), timestamp: daysAgo(2), action: 'Call Completed', actor: 'Sales Manager', details: 'Budget approval pending' },
        { id: generateId(), timestamp: daysAgo(1), action: 'Moved to Negotiation', actor: 'Admin' },
      ],
      notes: ['Client confirmed interest', 'Budget approval expected this week', 'Mobilization planned for 48h after PO'],
      proposalValue: 'SAR 1,450,000',
    },
    {
      rfqId: 'RFQ-004',
      referenceId: 'EPI-2026-0004',
      clientName: 'Omar Fadel',
      clientEmail: 'o.fadel@adnoc-eng.ae',
      companyName: 'ADNOC Engineering',
      serviceName: 'Stimulation & Nitrogen',
      dealHealthScore: 85,
      temperature: 'hot',
      closingStatus: 'active',
      daysWithoutResponse: 2,
      daysSinceProposal: 5,
      lastActivityDate: daysAgo(2),
      triggeredAction: evaluateTrigger('hot', 2, true),
      availableEmails: [],
      interventionRequired: true,
      interventionReason: 'High-probability deal — immediate personal contact required within 24h',
      timeline: [
        { id: generateId(), timestamp: daysAgo(5), action: 'Proposal Sent', actor: 'System' },
        { id: generateId(), timestamp: daysAgo(4), action: 'Email Opened (2x)', actor: 'Client' },
        { id: generateId(), timestamp: daysAgo(3), action: 'Portal Visit + Doc Download', actor: 'Client' },
        { id: generateId(), timestamp: daysAgo(2), action: 'Response Received', actor: 'Client', details: 'Requested timeline clarity' },
      ],
      notes: ['Client asking about mobilization timeline'],
      proposalValue: 'AED 890,000',
    },
    {
      rfqId: 'RFQ-009',
      referenceId: 'EPI-2026-0009',
      clientName: 'Hassan Khalil',
      clientEmail: 'h.khalil@taqa.ae',
      companyName: 'TAQA Energy',
      serviceName: 'Surface Well Testing',
      dealHealthScore: 88,
      temperature: 'hot',
      closingStatus: 'awaiting_confirmation',
      daysWithoutResponse: 1,
      daysSinceProposal: 10,
      lastActivityDate: daysAgo(1),
      triggeredAction: evaluateTrigger('hot', 1, true),
      availableEmails: [],
      interventionRequired: true,
      interventionReason: 'Contract stage — requires manual confirmation and document preparation',
      timeline: [
        { id: generateId(), timestamp: daysAgo(10), action: 'Proposal Sent', actor: 'System' },
        { id: generateId(), timestamp: daysAgo(8), action: 'Negotiation Started', actor: 'Admin' },
        { id: generateId(), timestamp: daysAgo(5), action: 'Commercial Terms Agreed', actor: 'Sales Manager' },
        { id: generateId(), timestamp: daysAgo(3), action: 'Contract Drafted', actor: 'Legal' },
        { id: generateId(), timestamp: daysAgo(1), action: 'Awaiting Client Signature', actor: 'System' },
      ],
      notes: ['Contract sent for signature', 'PO expected within 48h', 'Pre-mob checklist prepared'],
      proposalValue: 'AED 2,100,000',
    },
    // ─── WARM DEALS (CONTROLLED FOLLOW-UP) ─────────────────────────────────
    {
      rfqId: 'RFQ-002',
      referenceId: 'EPI-2026-0002',
      clientName: 'Sarah Mitchell',
      clientEmail: 's.mitchell@petrofac.com',
      companyName: 'Petrofac UAE',
      serviceName: 'Engineering & Fabrication',
      dealHealthScore: 62,
      temperature: 'warm',
      closingStatus: 'active',
      daysWithoutResponse: 4,
      daysSinceProposal: 8,
      lastActivityDate: daysAgo(4),
      triggeredAction: evaluateTrigger('warm', 4, true),
      availableEmails: [],
      interventionRequired: false,
      timeline: [
        { id: generateId(), timestamp: daysAgo(8), action: 'Proposal Sent', actor: 'System' },
        { id: generateId(), timestamp: daysAgo(6), action: 'Email Opened', actor: 'Client' },
        { id: generateId(), timestamp: daysAgo(5), action: 'Portal Visit', actor: 'Client' },
        { id: generateId(), timestamp: daysAgo(4), action: 'Button Click: Equipment List', actor: 'Client' },
      ],
      notes: ['Client reviewing technical scope'],
      proposalValue: 'AED 560,000',
    },
    {
      rfqId: 'RFQ-005',
      referenceId: 'EPI-2026-0005',
      clientName: 'Khalid Mostafa',
      clientEmail: 'k.mostafa@sapmt.com',
      companyName: 'SAPMT Construction',
      serviceName: 'Equipment Rental',
      dealHealthScore: 55,
      temperature: 'warm',
      closingStatus: 'active',
      daysWithoutResponse: 6,
      daysSinceProposal: 9,
      lastActivityDate: daysAgo(6),
      triggeredAction: evaluateTrigger('warm', 6, false),
      availableEmails: [],
      interventionRequired: false,
      timeline: [
        { id: generateId(), timestamp: daysAgo(9), action: 'Proposal Sent', actor: 'System' },
        { id: generateId(), timestamp: daysAgo(7), action: 'Email Opened', actor: 'Client' },
        { id: generateId(), timestamp: daysAgo(6), action: 'Portal Visit', actor: 'Client' },
      ],
      notes: [],
      proposalValue: 'SAR 320,000',
    },
    // ─── COLD DEALS (REACTIVATION OR CLOSE) ────────────────────────────────
    {
      rfqId: 'RFQ-003',
      referenceId: 'EPI-2026-0003',
      clientName: 'Fatima Al-Zahra',
      clientEmail: 'f.zahra@npcc.ae',
      companyName: 'NPCC Abu Dhabi',
      serviceName: 'Chemicals Supply',
      dealHealthScore: 28,
      temperature: 'cold',
      closingStatus: 'active',
      daysWithoutResponse: 14,
      daysSinceProposal: 16,
      lastActivityDate: daysAgo(14),
      triggeredAction: evaluateTrigger('cold', 14, false),
      availableEmails: [],
      interventionRequired: false,
      timeline: [
        { id: generateId(), timestamp: daysAgo(16), action: 'Proposal Sent', actor: 'System' },
        { id: generateId(), timestamp: daysAgo(14), action: 'Email Opened', actor: 'Client' },
        { id: generateId(), timestamp: daysAgo(10), action: 'Follow-Up 1 Sent', actor: 'System' },
        { id: generateId(), timestamp: daysAgo(7), action: 'Follow-Up 2 Sent', actor: 'System' },
      ],
      notes: ['No engagement after multiple follow-ups'],
      proposalValue: 'AED 180,000',
    },
    {
      rfqId: 'RFQ-006',
      referenceId: 'EPI-2026-0006',
      clientName: 'Tariq Rahman',
      clientEmail: 't.rahman@worleyparsons.com',
      companyName: 'Worley Parsons',
      serviceName: 'Logistics & Transportation',
      dealHealthScore: 18,
      temperature: 'cold',
      closingStatus: 'active',
      daysWithoutResponse: 16,
      daysSinceProposal: 18,
      lastActivityDate: daysAgo(16),
      triggeredAction: evaluateTrigger('cold', 16, false),
      availableEmails: [],
      interventionRequired: false,
      timeline: [
        { id: generateId(), timestamp: daysAgo(18), action: 'Proposal Sent', actor: 'System' },
        { id: generateId(), timestamp: daysAgo(16), action: 'Email Opened', actor: 'Client' },
        { id: generateId(), timestamp: daysAgo(12), action: 'Follow-Up 1 Sent', actor: 'System' },
        { id: generateId(), timestamp: daysAgo(7), action: 'Follow-Up 2 Sent', actor: 'System' },
        { id: generateId(), timestamp: daysAgo(3), action: 'Follow-Up 3 Sent', actor: 'System' },
      ],
      notes: ['Recommend archiving — no engagement'],
      proposalValue: 'SAR 95,000',
    },
  ];

  // Generate emails for each record
  return records.map(r => ({
    ...r,
    availableEmails: generateClosingEmails(r),
  }));
}
